import { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

import { Transition } from '@headlessui/react';
import clsx from 'clsx';

// Context
import { REMOVE_TOAST } from '@/utils/hooks/context/toast/actions';
import { useToastsContext } from '@/utils/hooks/context/toast/useToastsContext';
// Hooks
import useMarkAsReadNotification from '@/utils/hooks/notifications/useMarkAsReadNotification';
import useToastsNotifications from '@/utils/hooks/swr/notifications/useToastsNotifications';

// Utils
import { getCollapsedToastStyle, getExpandedToastStyle } from '@/utils/toasts/utils';

// Types
import type { AllNotificationsType } from '@/utils/hooks/context/toast/types';

// Lib
import { NOTIFICATIONS } from '@/lib/routes/privateRoutes';

// Components
import Notification from '@/components/base/Notification';
import NotificationsContainer from '@/components/composite/Notifications';

type ToastProps = {
    toastIndex: number;
    firstToastIndexVisible: number;
    nbMessages: number;
    isHover: boolean;
    notification: AllNotificationsType;
};

function SeeAllNotification({ show }: { show: boolean }) {
    const { t } = useTranslation();
    return (
        <span
            className={clsx(
                'absolute left-0 top-0 flex w-full -translate-y-full justify-end transition-opacity duration-300',
                {
                    'opacity-0': !show,
                    'opacity-100': show
                }
            )}>
            <Link
                href={NOTIFICATIONS}
                className="mb-2.5 rounded-full bg-white px-2.5 py-2 text-xs dark:bg-icoDarkMode-deepBlack">
                {t('commons:see_all_unread_notifications')}
            </Link>
        </span>
    );
}

const defaultDurationToast = 5000; // ms

function Toast({
    notification,
    toastIndex,
    firstToastIndexVisible,
    nbMessages,
    isHover
}: ToastProps) {
    const timer = useRef(null);
    const { dispatchToasts } = useToastsContext();
    const { markAsRead } = useMarkAsReadNotification();
    const { hasUnreadNotifications } = useToastsNotifications({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const [showToast, setShowToast] = useState(toastIndex >= firstToastIndexVisible);
    const [style, setStyle] = useState<{ transform?: string }>({});
    const isLastItem = toastIndex + 1 === nbMessages;
    const hideContent = !isLastItem && !isHover;

    const durationToast = notification?.options?.progressBar?.duration ?? defaultDurationToast;

    /**
     * Hide Toast
     */
    const hideToast = () => setShowToast(false);

    /**
     * Debounce the calc of new position when there is a new message to display
     */
    useDebounce(
        () => {
            if (isLastItem) {
                setStyle({
                    transform: 'translate3d(0, 0, 0)'
                });
                return;
            }

            const calcPosition = isHover
                ? getExpandedToastStyle(toastIndex, nbMessages)
                : getCollapsedToastStyle(toastIndex, nbMessages);
            setStyle(calcPosition);
        },
        50,
        [nbMessages, isHover]
    );

    useEffect(() => {
        setShowToast(toastIndex >= firstToastIndexVisible);
    }, [firstToastIndexVisible, toastIndex]);

    /**
     * Remove toast if it's not displayed
     * Use debounce to wait the end of the animation
     */
    useDebounce(
        () => {
            if (!showToast) {
                dispatchToasts({
                    type: REMOVE_TOAST,
                    payload: notification.id
                });
            }
        },
        300,
        [showToast]
    );

    /**
     * If progress bar deactivated:
     * Remove Toast after ${durationToast} secondes
     * Reset duration when the user hover it
     */
    useEffect(() => {
        if (!notification?.options?.progressBar?.show) {
            clearTimeout(timer.current);
            if (!isHover) {
                timer.current = setTimeout(() => {
                    setShowToast(false);
                }, durationToast);
            }
        }
        () => clearTimeout(timer.current);
         
    }, [isHover]);

    const styleWrapper: HTMLAttributes<HTMLDivElement>['style'] = {
        ...style,
        ...(!isLastItem && !isHover
            ? {
                  maxHeight: '94px',
                  overflow: 'hidden',
                  transitionDuration: '300ms'
              }
            : {
                  maxHeight: '116px',
                  transitionDuration: '500ms'
              })
    };

    return (
        <Transition
            appear={true}
            show={showToast}
            enter="!duration-500"
            enterFrom="opacity-75 translate-y-[calc(100%+70px)]"
            enterTo="opacity-100 translate-y-0"
            leave="!duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="absolute bottom-0 w-full transition-all" style={styleWrapper}>
                <div
                    className="toast-item duration"
                    data-testid={`toast-item-${toastIndex}-${!isHover ? 'collapsed' : 'expanded'}`}
                    data-id={toastIndex}>
                    <NotificationsContainer
                        notification={{
                            ...notification,
                            options: {
                                ...notification?.options,
                                wide: false,
                                hideContent,
                                hideToast: () => {
                                    dispatchToasts({
                                        type: REMOVE_TOAST,
                                        payload: notification.id
                                    });
                                },
                                progressBar: {
                                    ...notification?.options?.progressBar,
                                    pause: isHover,
                                    duration: durationToast,
                                    onAnimationEnd: hideToast
                                }
                            }
                        }}
                    />
                </div>
                {!hideContent ? (
                    <Notification.ReduceButton
                        onClick={() => {
                            markAsRead(notification.id);
                            hideToast();
                        }}
                    />
                ) : null}
                {firstToastIndexVisible === toastIndex && hasUnreadNotifications ? (
                    <SeeAllNotification show={isHover} />
                ) : null}
                <div className="left-0 h-2.5 w-full" />
            </div>
        </Transition>
    );
}

export default Toast;
